<!-- ============================================================
  IdMoneyField.vue (<id-money>) — ช่องกรอก "จำนวนเงิน" ของทั้งระบบ
  แทน <input type="number" step="0.01" @blur="x = $moneyInput(x)"> ทุกจุด

  ทำไมต้องมีคอมโพเนนต์นี้: input type=number แสดงคอมมาไม่ได้เลย (ค่าที่มี ',' ถือว่า
  invalid ช่องจะว่างทันที) ราคา 5,200 จึงโชว์เป็น '5200.00' ทั้งที่ทุกจุดที่แสดงผล
  ใช้ formatMoney → '5,200.00' ตัวเลขสองแบบในจอเดียวกันอ่านแล้วสับสน

  วิธีทำงาน — ฟอร์แมตเฉพาะ "ตอนไม่ได้โฟกัส" ไม่ยุ่งกับค่าที่ส่งออก:
    • ไม่โฟกัส → โชว์ formatMoney(v) = '5,200.00' (คอมมา + ทศนิยม 2 ตำแหน่งเสมอ)
    • โฟกัส    → โชว์ตัวเลขล้วนไม่มีคอมมา ('5200.00') พิมพ์/ลบง่าย เคอร์เซอร์ไม่กระโดด
    • ระหว่างพิมพ์ emit ค่าดิบไม่มีคอมมาทุกครั้ง (ค่าธรรมเนียม 3% ที่คำนวณสดจึงยังทำงาน)
    • ตอน blur emit เป็นสตริง 2 ตำแหน่ง เท่ากับ $moneyInput เป๊ะ ๆ
  ⇒ v-model ที่ผูกไว้ยัง "ไม่เคยมีคอมมา" โค้ดบันทึก/เทียบค่า/ส่ง backend เดิมไม่ต้องแก้เลย
     (ห้าม emit ค่าที่มีคอมมาเด็ดขาด — Number('5,200.00') = NaN และ multipart จะส่งสตริง
      ที่ backend เทียบกับ DECIMAL แล้วเห็นเป็น "แก้ไข" ทุกครั้ง)

  props ที่ไม่ได้ประกาศ (placeholder / readonly / disabled / class / style) ตกลงไปที่
  <input> ตรง ๆ ตามกลไก fallthrough — .fee-readonly กับ .fee-wrap .id-input จึงยังคุมได้เหมือนเดิม
  Props: modelValue   Emits: update:modelValue (สตริงตัวเลขล้วน หรือ '' เมื่อช่องว่าง)
============================================================ -->
<template>
  <input ref="el" class="id-input id-money" type="text" inputmode="decimal"
    :value="shown" @focus="onFocus" @input="onInput" @blur="onBlur">
</template>

<script>
import { formatMoney, normalizeMoneyInput } from '../../utils/money';

// เหลือแค่ตัวเลข + จุดทศนิยมจุดเดียว + ไม่เกิน 2 ตำแหน่ง (คอมมาที่ผู้ใช้วางมาก็ถูกตัดที่นี่)
// ไม่รับเครื่องหมายลบ — ทุกช่องเงินในระบบเป็น min="0" อยู่แล้ว และ backend ปฏิเสธค่าติดลบ
function sanitize(v) {
  let s = String(v === null || v === undefined ? '' : v).replace(/[^\d.]/g, '');
  const first = s.indexOf('.');
  if (first !== -1) s = s.slice(0, first + 1) + s.slice(first + 1).replace(/\./g, '');
  const dot = s.indexOf('.');
  if (dot !== -1) s = s.slice(0, dot + 3);
  return s;
}

export default {
  name: 'IdMoneyField',
  props: { modelValue: { default: '' } },
  emits: ['update:modelValue'],
  data() { return { focused: false, raw: '' }; },
  computed: {
    shown() {
      if (this.focused) return this.raw;
      const v = this.modelValue;
      if (v === null || v === undefined || String(v).trim() === '') return '';
      const n = Number(String(v).replace(/,/g, ''));
      return Number.isFinite(n) ? formatMoney(n) : String(v);
    },
  },
  methods: {
    onFocus() {
      // ช่องอ่านอย่างเดียว (เช่น ค่าธรรมเนียมที่ระบบคำนวณให้) ยังคลิกโฟกัสได้ แต่ต้องคงรูปแบบ
      // มีคอมมาไว้ ไม่งั้นแค่คลิกผ่านตัวเลขก็เปลี่ยนหน้าตาทั้งที่แก้อะไรไม่ได้
      const el = this.$refs.el;
      if (el && (el.readOnly || el.disabled)) return;
      this.raw = sanitize(this.modelValue);
      this.focused = true;
    },
    onInput(e) {
      const el = e.target;
      const typed = el.value;
      const clean = sanitize(typed);
      this.raw = clean;
      if (clean !== typed) {
        // ตัดอักขระที่ไม่ใช่ตัวเลขทิ้ง → ต้องเลื่อนเคอร์เซอร์กลับตามจำนวนที่ตัด
        // ไม่งั้นพิมพ์ผิดกลางช่องทีเดียวเคอร์เซอร์กระโดดไปท้ายสุด
        const pos = Math.max(0, (el.selectionStart || 0) - (typed.length - clean.length));
        el.value = clean;
        el.setSelectionRange(pos, pos);
      }
      this.$emit('update:modelValue', clean);
    },
    onBlur() {
      if (!this.focused) return;
      this.focused = false;
      const norm = normalizeMoneyInput(this.raw);
      this.raw = norm;
      // เทียบแบบสตริงเพื่อไม่ emit ซ้ำเมื่อค่าเท่าเดิม ('5200.00' = '5200.00')
      // แต่ '5200' → '5200.00' ต้อง emit เพราะรูปแบบที่เก็บต้องเป็น 2 ตำแหน่งเสมอ
      const cur = this.modelValue === null || this.modelValue === undefined ? '' : String(this.modelValue);
      if (norm !== cur) this.$emit('update:modelValue', norm);
    },
  },
};
</script>

<style scoped>
/* ตัวเลขความกว้างเท่ากันทุกหลัก — คอมมาไม่ขยับตำแหน่งตอนพิมพ์ */
.id-money { font-variant-numeric: tabular-nums; }
</style>
